import { Checkbox, Col, Form, Input, InputNumber, message, Row, Select, Space } from 'antd'
import * as PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useLazyQuery } from '@apollo/client'
import { toEnglishNumbers } from '../../../../shared/utils/helpers'
import AmountPriceField from '../../../common/formFields/AmountPriceField'
import { selectorDateFormat } from '../../settings/Print/formHelpers'
import { useUser } from '../../../../context/userContext'
import SEARCH_INSURANCE_POLICIES from '@dentolize/clinic-mobile/src/shared/store/queries/actions/insurancePolicies/searchInsurancePolicies'
import SearchField from '../../../common/searchFields/SearchField'
import SEARCH_INSURANCE_COMPANIES from '@dentolize/clinic-mobile/src/shared/store/queries/actions/insuranceCompanies/searchInsuranceCompanies'
import SEARCH_POLICY_CLASSES from '@dentolize/clinic-mobile/src/shared/store/queries/actions/policyClasses/searchPolicyClasses'
import GET_INSURANCE_COMPANY_DHS_HIERARCHY from '@dentolize/clinic-mobile/src/shared/store/queries/actions/insuranceCompanies/getInsuranceCompanyDhsInsuranceHierarchy'
import moment from 'moment'
import MomentDatePicker from '../../../../utils/timezone/MomentDatePicker'
import dayjs from 'dayjs'

// Insurance value form fields built from a source object — a hierarchy company
// node or the patient record (both share these scalar field names).
const insuranceValueFields = source => ({
  insuranceLimit: source?.insuranceLimit,
  insuranceUnlimited: !!source?.insuranceUnlimited,
  insurancePercentage: source?.insurancePercentage,
  fullDiscount: source?.insurancePercentage === 100,
  approvalRequired: source?.approvalRequired
})

const InsuranceForm = props => {
  const { form, selectedPatient, forPatientQrCode, visiblePatientFields, requiredPatientFields } = props
  const { setFieldsValue } = form

  const insuranceUnlimited = Form.useWatch('insuranceUnlimited', form)
  const fullDiscount = Form.useWatch('fullDiscount', form)
  const insurance = Form.useWatch('insurance', form)
  const forcePolicySelect = Form.useWatch('forcePolicySelect', form)
  const policyClass = Form.useWatch('policyClass', form)
  const insurancePolicy = Form.useWatch('insurancePolicy', form)
  const nationality = Form.useWatch('nationality', form)

  const policyRef = useRef(null)
  const policyClassRef = useRef(null)

  const [hierarchyData, setHierarchyData] = useState(null)

  const [getHierarchy] = useLazyQuery(GET_INSURANCE_COMPANY_DHS_HIERARCHY, {
    onCompleted: (response) => {
      if (response?.getInsuranceCompanyDhsInsuranceHierarchy?.success) {
        setHierarchyData(response?.getInsuranceCompanyDhsInsuranceHierarchy.data)
      }
    }
  })

  const initialInsuranceKey = selectedPatient?.insuranceCompany?.id ?? null
  const initialPolicyKey = selectedPatient?.insurancePolicy?.id ?? null
  const initialClassKey = selectedPatient?.policyClass?.id ?? null
  const initialNationality = selectedPatient?.nationality ?? null
  // Auto-fill a level only when its own selection actually changed (DHS Check
  // Insurance or manual select); hydrating a patient, or changing one level,
  // must not overwrite values owned by a level the user did not touch.
  const companyChanged = (insurance?.key ?? null) !== initialInsuranceKey
  const policyChanged = (insurancePolicy?.key ?? null) !== initialPolicyKey
  const classChanged = (policyClass?.key ?? null) !== initialClassKey
  const nationalityChanged = (nationality ?? null) !== initialNationality

  useEffect(() => {
    if (nationality === 'SA' && nationalityChanged) {
      setFieldsValue({ approvalRequired: true })
    }
  }, [nationality, nationalityChanged, setFieldsValue])

  useEffect(() => {
    if (insurance?.key) {
      getHierarchy({ variables: { input: { insuranceCompanyId: insurance.key } } })
    } else {
      setHierarchyData(null)
      form.resetFields(['insuranceLimit', 'insuranceUnlimited', 'insurancePercentage', 'approvalRequired', 'maxInsuranceLimit', 'insuranceStartMonth', 'forcePolicySelect'])
    }
  }, [insurance?.key, getHierarchy, form])

  useEffect(() => {
    if (!hierarchyData) return

    const company = hierarchyData
    const updates = {}

    if (companyChanged) {
      updates.insuranceStartMonth = company.insuranceStartMonth
      updates.forcePolicySelect = company.forcePolicySelect

      if (!policyClass?.key) {
        Object.assign(updates, insuranceValueFields(company))
      }

      if (company.priceList) {
        updates.priceList = { label: company.priceList.name, key: company.priceList.id }
      }
    }

    if (nationality === 'SA' && companyChanged) {
      updates.approvalRequired = true
    }

    if (company.policies) {
      let cls = null
      if (insurancePolicy?.key) {
        const policy = company.policies.find(p => p.id === insurancePolicy.key)
        if (policy && policyChanged) {
          if (policy.due) updates.insuranceEndDate = dayjs(policy.due)
          if (policy.inception) updates.insuranceStartMonth = dayjs(policy.inception).month()
        }
        cls = policy?.classes?.find(c => c.id === policyClass?.key)
      } else if (policyClass?.key) {
        for (const policy of company.policies) {
          const found = policy.classes?.find(c => c.id === policyClass.key)
          if (found) {
            cls = found
            break
          }
        }
      }

      if (cls && classChanged) {
        if (cls.maxLimit !== null && cls.maxLimit !== undefined) updates.maxInsuranceLimit = cls.maxLimit
        if (cls.insurancePercentage !== null && cls.insurancePercentage !== undefined) {
          updates.insurancePercentage = cls.insurancePercentage
          updates.fullDiscount = cls.insurancePercentage === 100
        }
        if (cls.insuranceLimit !== null && cls.insuranceLimit !== undefined) updates.insuranceLimit = cls.insuranceLimit
        if (cls.insuranceUnlimited !== null && cls.insuranceUnlimited !== undefined) updates.insuranceUnlimited = !!cls.insuranceUnlimited
        if (cls.approvalRequired !== null && cls.approvalRequired !== undefined) updates.approvalRequired = cls.approvalRequired
      }
    }

    setFieldsValue(updates)
  }, [hierarchyData, insurancePolicy?.key, policyClass?.key, setFieldsValue])

  useEffect(() => {
    if (insuranceUnlimited) {
      setFieldsValue({ insuranceLimit: null })
    }
  }, [insuranceUnlimited])

  useEffect(() => {
    if (fullDiscount) {
      setFieldsValue({ insurancePercentage: 100 })
    }
  }, [fullDiscount])

  const { t } = useTranslation()
  const { user } = useUser()

  const onSelectInsurance = e => {
    if (!e?.item) {
      setFieldsValue({
        insurancePolicy: null,
        policyClass: null
      })
      return
    }

    const newInsurance = e.item

    if (newInsurance.forcePolicySelect) {
      setFieldsValue({
        forcePolicySelect: newInsurance.forcePolicySelect,
        insuranceStartMonth: newInsurance.insuranceStartMonth,
        insurancePolicy: newInsurance.id !== insurance?.key ? null : undefined,
        policyClass: newInsurance.id !== insurance?.key ? null : undefined
      })
    } else {
      setFieldsValue({
        insuranceLimit: newInsurance.insuranceLimit,
        insuranceUnlimited: !!newInsurance.insuranceUnlimited,
        insurancePercentage: newInsurance.insurancePercentage,
        fullDiscount: newInsurance.insurancePercentage === 100,
        approvalRequired: newInsurance.approvalRequired,
        insuranceStartMonth: newInsurance.insuranceStartMonth,
        forcePolicySelect: newInsurance.forcePolicySelect,
        insurancePolicy: newInsurance.id !== insurance?.key ? null : undefined,
        policyClass: newInsurance.id !== insurance?.key ? null : undefined,
        maxInsuranceLimit: null
      })
    }

    if (policyRef.current) {
      policyRef.current.focus()
    }
    if (newInsurance.priceList) {
      message.success(t('app.priceListUpdated'))
      setFieldsValue({ priceList: { label: newInsurance.priceList.name, key: newInsurance.priceList.id } })
    }
  }

  const handleNewInsuranceCompany = obj => {
    setFieldsValue(obj)
    if (obj) onSelectInsurance(obj.insurance)
  }

  const onSelectInsurancePolicy = e => {
    if (!e?.item) {
      const updates = { policyClass: null, maxInsuranceLimit: null }
      if (policyClass?.key) {
        if (hierarchyData) Object.assign(updates, insuranceValueFields(hierarchyData))
        if (nationality === 'SA') updates.approvalRequired = true
      }
      setFieldsValue(updates)
      return
    }

    const newInsurancePolicy = e.item

    setFieldsValue({
      insuranceEndDate: newInsurancePolicy.due ? moment(newInsurancePolicy.due) : undefined,
      insuranceStartMonth: newInsurancePolicy.inception ? moment(newInsurancePolicy.inception).month() : undefined,
      policyClass: newInsurancePolicy.id !== insurancePolicy?.key ? null : undefined,
      maxInsuranceLimit: newInsurancePolicy.id !== insurancePolicy?.key ? null : undefined
    })

    if (policyClassRef.current) {
      policyClassRef.current.focus()
    }
    if (newInsurancePolicy.priceList) {
      message.success(t('app.priceListUpdated'))
      setFieldsValue({ priceList: { label: newInsurancePolicy.priceList.name, key: newInsurancePolicy.priceList.id } })
    }
  }

  const onSelectPolicyClass = e => {
    if (!e?.item) {
      // On clear, class-owned values fall back to the Insurance Company defaults
      // (never the patient's own overwritten values). DHS-only: hierarchyData is
      // null for non-DHS tenants, so values are left as-is like main.
      const updates = { maxInsuranceLimit: null, ...(hierarchyData ? insuranceValueFields(hierarchyData) : {}) }
      if (nationality === 'SA') updates.approvalRequired = true
      setFieldsValue(updates)
      return
    }

    const policyClass = e.item

    setFieldsValue({
      insuranceLimit: policyClass.insuranceLimit || null,
      insuranceUnlimited: !!policyClass.insuranceUnlimited,
      insurancePercentage: policyClass.insurancePercentage,
      fullDiscount: policyClass.insurancePercentage === 100,
      approvalRequired: policyClass.approvalRequired,
      maxInsuranceLimit: policyClass.maxLimit || null,
      insurancePolicy: {
        key: policyClass.insurancePolicy.id,
        label: policyClass.insurancePolicy.holderName
      }
    })
  }

  const handleNewInsurancePolicy = obj => {
    setFieldsValue(obj)
    if (obj) onSelectInsurancePolicy(obj.insurancePolicy)
  }

  const handleNewPolicyClass = obj => {
    setFieldsValue(obj)
    if (obj) onSelectPolicyClass(obj.policyClass)
  }

  return (
    <Col md={insurance ? 24 : 8} xs={24}>
      <Form.Item name="forcePolicySelect" hidden>
        <Input />
      </Form.Item>
      <Row type="flex" gutter={16}>
        {!forPatientQrCode ? (
          <>
            <Col md={insurance ? 8 : 24} xs={24}>
              <SearchField
                fieldName="insurance"
                onSelect={onSelectInsurance}
                rules={
                  requiredPatientFields.includes('insuranceDetails') ? [{ required: true, message: t('app.fieldRequired') }] : []
                }
                GET_QUERY={SEARCH_INSURANCE_COMPANIES}
                label={t('calendar.insuranceCompany')}
                dataName="searchInsuranceCompanies"
                setFieldsValue={handleNewInsuranceCompany}
                allowClear
              />
            </Col>
            {insurance ? (
              <>
                <Col md={8} xs={24}>
                  <SearchField
                    patientSearchFieldRef={policyRef}
                    fieldName="insurancePolicy"
                    onSelect={onSelectInsurancePolicy}
                    rules={forcePolicySelect || policyClass ? [{ required: true, message: t('app.fieldRequired') }] : []}
                    GET_QUERY={SEARCH_INSURANCE_POLICIES}
                    label={t('settings.insurance.insurancePolicy')}
                    dataName="searchInsurancePolicies"
                    passToQuery={{ insuranceCompany: insurance.key }}
                    setFieldsValue={handleNewInsurancePolicy}
                    allowClear
                    disableAddNew={!insurance}
                  />
                </Col>
                <Col md={8} xs={24}>
                  <SearchField
                    patientSearchFieldRef={policyClassRef}
                    fieldName="policyClass"
                    onSelect={onSelectPolicyClass}
                    rules={forcePolicySelect ? [{ required: true, message: t('app.fieldRequired') }] : []}
                    GET_QUERY={SEARCH_POLICY_CLASSES}
                    label={t('settings.insurance.policyClass')}
                    dataName="searchPolicyClasses"
                    passToQuery={{ insuranceCompany: insurance.key, insurancePolicy: insurancePolicy?.key }}
                    setFieldsValue={handleNewPolicyClass}
                    allowClear
                    disableAddNew={!insurance || !insurancePolicy}
                  />
                </Col>
              </>
            ) : null}
          </>
        ) : null}
        {(insurance || forPatientQrCode) && (
          <>
            {!forPatientQrCode && (
              <>
                <Col md={8} xs={24}>
                  <Space wrap size="large">
                    <AmountPriceField
                      formName="insuranceForm"
                      name="insuranceLimit"
                      label={t('calendar.insuranceLimit')}
                      disabled={insuranceUnlimited || !!policyClass}
                      step={100}
                      rules={[{ type: 'number', required: !insuranceUnlimited, message: t('calendar.insuranceLimitErr') }]}
                    />
                    <Form.Item name="insuranceUnlimited" label={t('calendar.unlimited')} valuePropName="checked">
                      <Checkbox disabled={!!policyClass} data-testid="insuranceForm-insuranceUnlimited" />
                    </Form.Item>
                  </Space>
                </Col>
                <Col md={8} xs={24}>
                  <Space wrap size="large">
                    <Form.Item
                      label={t('calendar.insuranceDiscount')}
                      name="insurancePercentage"
                      rules={[{ required: !fullDiscount, message: t('app.fieldRequired') }]}
                    >
                      <InputNumber
                        data-testid="insuranceForm-insurancePercentage"
                        inputMode="numeric"
                        min={0}
                        max={100}
                        addonAfter="%"
                        parser={value => toEnglishNumbers(value)}
                        disabled={fullDiscount || !!policyClass}
                        precision={0}
                        step={10}
                      />
                    </Form.Item>
                    <Form.Item name="fullDiscount" label={'100%'} valuePropName="checked">
                      <Checkbox disabled={!!policyClass} data-testid="insuranceForm-fullDiscount" />
                    </Form.Item>
                  </Space>
                </Col>
                {policyClass ? (
                  <Col md={8} xs={24}>
                    <AmountPriceField
                      formName="insuranceForm"
                      name="maxInsuranceLimit"
                      label={t('settings.insurance.maxLimit')}
                      step={10}
                      disabled={!!policyClass}
                      rules={[{ required: requiredPatientFields.includes('maxInsuranceLimit'), message: t('app.fieldRequired') }]}
                    />
                  </Col>
                ) : null}
                <Col span={24}>
                  <br />
                </Col>
              </>
            )}
            {visiblePatientFields.includes('insuranceNumber') && (
              <Col md={8} xs={24}>
                <Form.Item
                  label={t('app.insuranceNumber')}
                  name="insuranceNumber"
                  rules={[
                    { max: 100, message: t('calendar.companyNameLong') },
                    { required: requiredPatientFields.includes('insuranceNumber'), message: t('app.fieldRequired') }
                  ]}
                >
                  <Input data-testid="insuranceForm-insuranceNumber" autoComplete="off" max={100} />
                </Form.Item>
              </Col>
            )}
            {visiblePatientFields.includes('insuranceGroup') && (
              <Col md={8} xs={24}>
                <Form.Item
                  label={t('app.insuranceGroup')}
                  name="insuranceGroup"
                  rules={[
                    { max: 100, message: t('calendar.companyNameLong') },
                    { required: requiredPatientFields.includes('insuranceGroup'), message: t('app.fieldRequired') }
                  ]}
                >
                  <Input data-testid="insuranceForm-insuranceGroup" autoComplete="off" max={100} />
                </Form.Item>
              </Col>
            )}
            {visiblePatientFields.includes('insuranceCRT') && (
              <Col md={8} xs={24}>
                <Form.Item
                  label={t('app.insuranceCRT')}
                  name="insuranceCRT"
                  rules={[
                    { max: 100, message: t('calendar.companyNameLong') },
                    { required: requiredPatientFields.includes('insuranceCRT'), message: t('app.fieldRequired') }
                  ]}
                >
                  <Input data-testid="insuranceForm-insuranceCRT" autoComplete="off" max={100} />
                </Form.Item>
              </Col>
            )}
            {visiblePatientFields.includes('insuranceEndDate') && (
              <Col md={8} xs={24}>
                <Form.Item
                  label={t('app.insuranceEndDate')}
                  name="insuranceEndDate"
                  rules={[{ required: requiredPatientFields.includes('insuranceEndDate'), message: t('app.fieldRequired') }]}
                >
                  <MomentDatePicker
                    disabled={!!policyClass}
                    data-testid="insuranceForm-insuranceEndDate"
                    allowClear={false}
                    format={{
                      format: selectorDateFormat(user)[1],
                      type: 'mask'
                    }}
                    className="full-width"
                  />
                </Form.Item>
              </Col>
            )}
            {visiblePatientFields.includes('insuranceStartMonth') && (
              <Col md={8} xs={24}>
                <Form.Item
                  label={t('app.insuranceStartMonth')}
                  name="insuranceStartMonth"
                  extra={t('app.ifEmpty')}
                  rules={[{ required: requiredPatientFields.includes('insuranceStartMonth'), message: t('app.fieldRequired') }]}
                >
                  <Select
                    data-testid="insuranceForm-insuranceStartMonth"
                    className="fixArrowSelection"
                    showAction={['click', isMobile ? '' : 'focus']}
                    allowClear
                    disabled={!!policyClass}
                  >
                    {new Array(12).fill(1).map((_, i) => (
                      <Select.Option value={i} key={i}>
                        {moment().month(i).format('MMMM')}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            {!forPatientQrCode && (
              <Col md={8} xs={24}>
                <Form.Item name="approvalRequired" valuePropName="checked" label={t('app.approvalRequired')}>
                  <Checkbox disabled={!!policyClass} data-testid="insuranceForm-approvalRequired" />
                </Form.Item>
              </Col>
            )}
          </>
        )}
      </Row>
    </Col>
  )
}

InsuranceForm.propTypes = {
  form: PropTypes.object.isRequired,
  selectedPatient: PropTypes.object.isRequired,
  forPatientQrCode: PropTypes.bool,
  requiredPatientFields: PropTypes.array.isRequired,
  visiblePatientFields: PropTypes.array.isRequired
}

export default InsuranceForm
